/**
 * Global API Interceptor — single source of truth for 401 handling.
 *
 * HOW IT WORKS:
 * 1. Attaches to the global `axios` default instance, so EVERY axios call
 *    (legacy services, Redux actions, etc.) gets the token injected and
 *    401 handling for free.
 * 2. On a 401 response it tries to refresh the Salesforce token ONCE.
 *    - While the refresh is in-flight, all other failing requests are
 *      queued and replayed automatically once the new token arrives.
 *    - If refresh fails ➜ single "Session Expired" alert + redirect to /login.
 * 3. The refresh call itself uses a **plain axios instance** (no interceptors)
 *    so a 401 from the token-endpoint cannot trigger an infinite loop.
 * 4. A debounce flag ensures only ONE "Session Expired" alert is ever
 *    dispatched within a 5-second window, no matter how many parallel
 *    requests fail.
 *
 * IMPORTANT: apiClient.js's per-instance interceptor is only responsible
 * for logging.  It must NOT attempt its own 401 handling — this class owns it.
 */

import axios from 'axios';
import { getNodeServerUrl } from '../utils/getNodeUrl';
import { store } from '../Redux/store';

// ── Plain axios instance for token refresh (NO interceptors) ───────────
const refreshClient = axios.create();

class APIInterceptor {
  constructor() {
    this.isRefreshing = false;
    this.failedQueue = []; // { resolve, reject } pairs
    this._sessionAlertShown = false; // dedup flag
    this._setupInterceptors();
  }

  // ───────────────────────────────────────────────────────────────────────
  // INTERCEPTORS
  // ───────────────────────────────────────────────────────────────────────

  _setupInterceptors() {
    // ── REQUEST: inject token ──────────────────────────────────────────
    axios.interceptors.request.use(
      (config) => {
        // Skip token injection for the refresh call itself
        if (config._isRefreshRequest) return config;

        const accessToken = store.getState()?.tokens?.accessToken;
        if (accessToken) {
          config.headers.Authorization = `Bearer ${accessToken}`;
        }
        return config;
      },
      (error) => Promise.reject(error),
    );

    // ── RESPONSE: handle 401 ──────────────────────────────────────────
    axios.interceptors.response.use(
      (response) => response,
      async (error) => {
        const originalRequest = error.config;

        // Only handle genuine 401 Unauthorized
        if (error.response?.status !== 401 || originalRequest?._retry) {
          return Promise.reject(error);
        }

        // Don't attempt refresh on the login page — no valid session yet
        if (window.location.pathname === '/login') {
          return Promise.reject(error);
        }

        originalRequest._retry = true;

        // ── Already refreshing? Queue this request. ────────────────────
        if (this.isRefreshing) {
          return new Promise((resolve, reject) => {
            this.failedQueue.push({ resolve, reject });
          }).then((newToken) => {
            originalRequest.headers.Authorization = `Bearer ${newToken}`;
            return axios(originalRequest);
          });
        }

        // ── Start refresh ──────────────────────────────────────────────
        this.isRefreshing = true;

        try {
          const username = store.getState()?.userDetails?.details?.Email;
          if (!username) throw new Error('No username for token refresh');

          console.log('🔄 Refreshing token…');
          const nodeBaseUrl = getNodeServerUrl();
          const { data } = await refreshClient.post(
            `${nodeBaseUrl}/user/get-salesforce-access-token`,
            { username },
          );

          const newToken = data?.access_token;
          if (!newToken) throw new Error('Empty token in refresh response');

          // Persist to Redux
          store.dispatch({
            type: 'GET_USER_ACCESS_TOKEN_SUCCESS',
            payload: {
              accessToken: newToken,
              creationDate: new Date().toISOString(),
            },
          });

          console.log('✅ Token refreshed');

          // Replay queued requests
          this._processQueue(null, newToken);

          // Retry the original request
          originalRequest.headers.Authorization = `Bearer ${newToken}`;
          return axios(originalRequest);
        } catch (refreshError) {
          console.error('❌ Token refresh failed:', refreshError?.message);

          // Reject every queued request
          this._processQueue(refreshError, null);

          // Show ONE alert + redirect (debounced)
          this._handleAuthFailure();

          return Promise.reject(refreshError);
        } finally {
          this.isRefreshing = false;
        }
      },
    );
  }

  // ───────────────────────────────────────────────────────────────────────
  // HELPERS
  // ───────────────────────────────────────────────────────────────────────

  /** Resolve or reject every queued request */
  _processQueue(error, token) {
    this.failedQueue.forEach(({ resolve, reject }) => {
      if (error) {
        reject(error);
      } else {
        resolve(token);
      }
    });
    this.failedQueue = [];
  }

  /**
   * Single "Session Expired" alert, then redirect.
   * Debounced so if multiple requests fail while the refresh is also
   * failing, the user sees exactly ONE message.
   */
  _handleAuthFailure() {
    if (this._sessionAlertShown) return;
    this._sessionAlertShown = true;

    // Also suppress the legacy sessionExpireTimeout flag so both systems stay in sync
    try {
      const actions = require('../Redux/actions/utility-actions');
      if (typeof actions._suppressSessionExpireAlert === 'function') {
        actions._suppressSessionExpireAlert();
      }
    } catch (_) {}

    // Dispatch a single alert via the existing Redux alert system
    const { setAlert } = require('../Redux/actions/alert-action');
    store.dispatch(setAlert('Session Expired, Please Login again', 'error'));

    // Dispatch logout
    store.dispatch({ type: 'LOGOUT' });

    // Redirect
    if (window.location.pathname !== '/login') {
      window.location.href = '/login';
    }

    // Reset debounce after 5 s so a *future* session can still show the alert
    setTimeout(() => {
      this._sessionAlertShown = false;
    }, 5000);
  }

  // ───────────────────────────────────────────────────────────────────────
  // PUBLIC API (used very rarely, mostly for debugging)
  // ───────────────────────────────────────────────────────────────────────

  isCurrentlyRefreshing() {
    return this.isRefreshing;
  }

  getQueuedRequestsCount() {
    return this.failedQueue.length;
  }
}

// Singleton — initialized on import
export const apiInterceptor = new APIInterceptor();
export default apiInterceptor;